import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useCreateDomain } from '../../domains/hooks/useDomains';
import { TestChat, UrlDiscovery } from '../../domains/components';
import { useAuthStore } from '../../../stores/authStore';
import { api, accessDBApi } from '../../../services/api';
import { getDomainDetailPath } from '../../../config/routes';
import { isValidDomain, generateEmbedCode } from '../../../lib/utils';
import { WIDGET_URL } from '../../../config/constants';
import { InstallGuideModal } from '../../domains/components/InstallGuideModal';

const steps = ['サイトを教える', 'AIが学習', 'ためす', '設置'];

const domainSchema = z.object({
  domain: z.string().min(1, 'ドメインを入力してください')
    .refine((val) => isValidDomain(val), { message: '有効なドメイン名を入力してください' }),
});

type DomainFormData = z.infer<typeof domainSchema>;

const getPathFromUrl = (url: string) => {
  try { return new URL(url).pathname; } catch { return url; }
};

export const OnboardingWizard: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [createdDomainId, setCreatedDomainId] = useState<string | null>(null);
  const [createdDomainName, setCreatedDomainName] = useState('');
  const [discoveredUrls, setDiscoveredUrls] = useState<string[]>([]);
  const [crawlFiles, setCrawlFiles] = useState<Array<{ url?: string; sync_status?: string }>>([]);
  const [crawlStarted, setCrawlStarted] = useState(false);
  const [discoverDone, setDiscoverDone] = useState(false);
  const [discoverError, setDiscoverError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [onboardingPhase, setOnboardingPhase] = useState<'discovering' | 'selecting' | 'crawling'>('discovering');
  const [embedCopied, setEmbedCopied] = useState(false);
  const [installGuideOpen, setInstallGuideOpen] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const navigate = useNavigate();
  const { updateUser } = useAuthStore();

  const { mutate: createDomain, isPending: isCreating } = useCreateDomain({
    onSuccessOverride: (data) => {
      setCreatedDomainId(data.id);
      setCreatedDomainName(data.domain);
      setActiveStep(1);
    },
  });

  const { control, handleSubmit, formState: { errors } } = useForm<DomainFormData>({
    resolver: zodResolver(domainSchema),
    defaultValues: { domain: '' },
  });

  useEffect(() => {
    if (activeStep !== 1 || !createdDomainId) return;

    let cancelled = false;

    const run = async () => {
      try {
        const res = await accessDBApi.post(`/v1/domains/${createdDomainId}/discover-urls`, {});
        const rawUrls: Array<{ url?: string } | string> = res.data?.data?.urls ?? [];
        const urls: string[] = rawUrls
          .map((u) => (typeof u === 'string' ? u : u?.url))
          .filter((u): u is string => Boolean(u));
        if (!cancelled) {
          setDiscoveredUrls(urls);
          setDiscoverDone(true);

          if (urls.length > 0) {
            if (urls.length <= 50) {
              const BATCH_SIZE = 200;
              for (let i = 0; i < urls.length; i += BATCH_SIZE) {
                await accessDBApi.post(`/v1/domains/${createdDomainId}/crawl/batch`, { urls: urls.slice(i, i + BATCH_SIZE) });
              }
              if (!cancelled) setCrawlStarted(true);
            } else {
              if (!cancelled) {
                setOnboardingPhase('selecting');
              }
              return;
            }
          } else {
            if (!cancelled) {
              setDiscoverError(true);
              try {
                await accessDBApi.delete(`/v1/domains/${createdDomainId}`);
              } catch { /* ベストエフォート */ }
            }
            return;
          }
        }
      } catch {
        if (!cancelled) {
          setDiscoverError(true);
          return;
        }
      }

      const poll = setInterval(async () => {
        try {
          const res = await accessDBApi.get(`/v1/domains/${createdDomainId}`);
          const files: Array<{ url?: string; sync_status?: string }> = res.data?.data?.crawl_files ?? [];
          if (!cancelled) {
            setCrawlFiles(files);
            const N = files.length;
            const isTerminal = (s: string | undefined) => s === 'synced' || s === 'error';
            if (N > 0 && files.every(f => isTerminal(f.sync_status))) {
              clearInterval(poll);
              pollRef.current = null;
              if (!cancelled) setActiveStep(2);
            }
          }
        } catch { /* poll継続 */ }
      }, 3000);
      pollRef.current = poll;
    };

    run();

    return () => {
      cancelled = true;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [activeStep, createdDomainId, retryCount]);

  const completeOnboarding = async () => {
    try {
      await api.put('/users/me', { has_completed_onboarding: true });
      updateUser({ has_completed_onboarding: true });
    } catch { /* ignore */ }
    navigate(`${getDomainDetailPath(createdDomainId || '')}?tab=overview`);
  };

  const onDomainSubmit = (data: DomainFormData) => { createDomain(data); };

  const handleDiscoverRetry = () => {
    setDiscoverError(false);
    setDiscoverDone(false);
    setDiscoveredUrls([]);
    setCrawlFiles([]);
    setCrawlStarted(false);
    setRetryCount(c => c + 1);
  };

  const handleChangeUrl = () => {
    setActiveStep(0);
    setDiscoverError(false);
    setDiscoverDone(false);
    setCreatedDomainId(null);
  };

  const handleEmbedCopy = () => {
    const code = generateEmbedCode(
      sessionStorage.getItem(`eb_apikey_${createdDomainId}`) || ''
    );
    navigator.clipboard.writeText(code).then(() => {
      setEmbedCopied(true);
      setTimeout(() => setEmbedCopied(false), 2000);
    });
  };

  const step2N = discoveredUrls.length;
  const step2X = crawlFiles.filter(f => f.sync_status === 'synced').length;
  const step2ProgressPct = step2N > 0 ? Math.round((step2X / step2N) * 100) : 0;

  // suppress unused warning — kept for potential future use
  void discoverDone;
  void crawlStarted;
  void createdDomainName;

  return (
    <div data-testid="onboarding-wizard" className="max-w-[800px] mx-auto py-6 md:py-12 flex flex-col items-center px-4">

      {/* ===== Desktop Stepper (md+) ===== */}
      <div className="hidden md:flex items-center justify-center mb-11 w-full max-w-[680px]" data-testid="onboarding-stepper">
        {steps.map((label, index) => (
          <React.Fragment key={label}>
            {index > 0 && (
              <div
                className="flex-1 min-w-[48px] mb-5"
                style={{ height: '2px', background: index <= activeStep ? '#0a7a72' : '#E2E8F0' }}
              />
            )}
            <div className="flex flex-col items-center">
              <div
                style={{
                  width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '15px', fontWeight: 700, marginBottom: '8px',
                  background: index <= activeStep ? '#0a7a72' : '#E2E8F0',
                  color: index <= activeStep ? '#fff' : '#94A3B8',
                  boxShadow: index === activeStep ? '0 0 0 4px rgba(10,122,114,0.15)' : 'none',
                }}
              >
                {index < activeStep ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                ) : (
                  index + 1
                )}
              </div>
              <span style={{ fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap', color: index <= activeStep ? '#0a7a72' : '#94A3B8' }}>
                {label}
              </span>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* ===== SP Segment Bar (<md) ===== */}
      <div className="flex md:hidden gap-1.5 justify-center mb-5 w-full max-w-[200px]">
        {steps.map((_, i) => (
          <div
            key={i}
            style={{ height: '6px', borderRadius: '3px', flex: 1, maxWidth: '48px', background: i <= activeStep ? '#0a7a72' : '#E2E8F0' }}
          />
        ))}
      </div>

      {/* ===== Step 1: URL入力 ===== */}
      {activeStep === 0 && (
        <div
          data-testid="onboarding-step1-domain-form"
          style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '16px', width: '100%', maxWidth: '660px', padding: '44px 52px', boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)' }}
          className="sm:p-[44px_52px] p-[28px_22px]"
        >
          <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#0F172A', marginBottom: '10px', lineHeight: '1.4' }}>
            あなたのサイトのURLを教えてください
          </h2>
          <p style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.7', marginBottom: '28px' }}>
            入力したサイトをAIが読み込み、お客様の質問に自動で答えるチャットボットを作ります。
          </p>

          <form onSubmit={handleSubmit(onDomainSubmit)}>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '8px', display: 'block' }}>
                サイトのURL
              </label>
              <Controller name="domain" control={control} render={({ field }) => (
                <>
                  <input
                    {...field}
                    type="text"
                    data-testid="domain-input"
                    style={{ width: '100%', padding: '14px 16px', fontSize: '16px', border: '1.5px solid #E2E8F0', borderRadius: '10px', outline: 'none', fontFamily: 'inherit', color: '#0F172A', background: '#fff', boxSizing: 'border-box' }}
                    placeholder="https://example.co.jp"
                  />
                  {errors.domain && <p className="text-sm text-red-500 mt-1">{errors.domain.message}</p>}
                </>
              )} />
              <p style={{ fontSize: '13px', color: '#0a7a72', marginTop: '8px' }}>
                トップページのURLを入力してください。サイト内のページを自動で探します。
              </p>
            </div>

            <button
              type="submit"
              data-testid="domain-submit-button"
              disabled={isCreating}
              style={{ background: '#0a7a72', color: '#fff', border: 'none', borderRadius: '10px', padding: '16px 28px', fontSize: '16px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px rgba(10,122,114,0.25)', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
              {isCreating ? <Loader2 className="h-5 w-5 animate-spin" /> : 'サイトを読み込む'}
            </button>
          </form>

        </div>
      )}

      {/* ===== Step 2: AIが学習 ===== */}
      {activeStep === 1 && createdDomainId && (
        discoverError ? (
          <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '16px', width: '100%', maxWidth: '660px', padding: '44px 52px', textAlign: 'center' }}>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>😔</div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: '#0F172A', marginBottom: '8px' }}>ページが見つかりませんでした</div>
            <div style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.7', marginBottom: '28px' }}>
              URLが正しいか確認して、もう一度お試しください。
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={handleDiscoverRetry} style={{ background: '#fff', color: '#0a7a72', border: '1.5px solid #0a7a72', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>もう一度試す</button>
              <button onClick={handleChangeUrl} style={{ background: '#0a7a72', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>URLを変更する</button>
            </div>
          </div>
        ) : onboardingPhase === 'selecting' ? (
          <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '16px', width: '100%', maxWidth: '660px', padding: '32px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#0F172A', marginBottom: '8px' }}>
              学習するページを選んでください
            </h2>
            <p style={{ fontSize: '14px', color: '#64748B', marginBottom: '20px' }}>
              {discoveredUrls.length}件のページが見つかりました。学習させたいページを選んでください。
            </p>
            <UrlDiscovery
              domainId={createdDomainId}
              domainUrl=""
              initialUrls={discoveredUrls.map(u => ({ url: u, source: 'crawl' as const }))}
              onComplete={() => setActiveStep(2)}
              onSkip={() => setActiveStep(2)}
            />
          </div>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '16px', width: '100%', maxWidth: '660px', padding: '44px 52px', boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)' }}>
            {/* spinner in teal circle */}
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#F0FDFA', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
              <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#0a7a72' }} />
            </div>
            <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#0F172A', marginBottom: '8px', lineHeight: '1.4', textAlign: 'center' }}>
              AIがサイトを学習しています
            </h2>
            <p style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.7', marginBottom: '28px', textAlign: 'center' }}>
              ページを読み込んで、お客様の質問に答えられるよう準備しています。
            </p>

            {/* progress bar */}
            <div style={{ height: '8px', background: '#E2E8F0', borderRadius: '4px', overflow: 'hidden', marginBottom: '8px' }}>
              <div style={{ height: '100%', background: '#0a7a72', borderRadius: '4px', width: `${step2ProgressPct}%`, transition: 'width 0.5s' }} />
            </div>
            <p style={{ fontSize: '13px', color: '#64748B', textAlign: 'center', marginBottom: '24px' }}>
              {step2N > 0 ? `${step2X} / ${step2N} ページを学習済み` : '準備中...'}
            </p>

            {/* page list */}
            {crawlFiles.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {crawlFiles.slice(0, 5).map((file, i) => {
                  const isSynced = file.sync_status === 'synced';
                  const path = getPathFromUrl(file.url || '');
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: '#F8FAFC', borderRadius: '8px', fontSize: '14px' }}>
                      <span style={{
                        fontSize: '12px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', flexShrink: 0,
                        background: isSynced ? '#DCFCE7' : '#FEF3C7',
                        color: isSynced ? '#16A34A' : '#D97706',
                      }}>
                        {isSynced ? '完了' : '処理中'}
                      </span>
                      <span style={{ color: '#334155', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {path || file.url || ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )
      )}

      {/* ===== Step 3: 動作をためす ===== */}
      {activeStep === 2 && createdDomainId && (
        <div
          data-testid="onboarding-step3-test-chat"
          style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '16px', width: '100%', maxWidth: '660px', padding: '28px 32px', boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)' }}
          className="sm:p-[28px_32px] p-[20px_16px]"
        >
          <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#0F172A', marginBottom: '8px', lineHeight: '1.4' }}>
            動作をためしてみましょう
          </h2>
          <p style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.7', marginBottom: '20px' }}>
            お客様になったつもりで質問してみてください。学習した内容から自動で回答します。
          </p>

          {/* chat-frame */}
          <div style={{ border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden', marginBottom: '16px' }}>
            <TestChat domainId={createdDomainId} />
          </div>

          {/* 継続学習注記 */}
          <div
            data-testid="onboarding-continuous-learning-note"
            style={{ fontSize: '12px', color: '#94A3B8', textAlign: 'center', marginBottom: '20px' }}
          >
            基本ページは学習済みです。残りのページも継続して学習中です
          </div>

          <button
            onClick={() => setActiveStep(3)}
            style={{ background: '#0a7a72', color: '#fff', border: 'none', borderRadius: '10px', padding: '16px 28px', fontSize: '16px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px rgba(10,122,114,0.25)', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            うまく答えられている。次へ
          </button>
        </div>
      )}

      {/* ===== Step 4: 設置 ===== */}
      {activeStep === 3 && createdDomainId && (
        <div
          data-testid="onboarding-step4-embed"
          style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '16px', width: '100%', maxWidth: '660px', padding: '44px 52px', boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)' }}
          className="sm:p-[44px_52px] p-[28px_22px]"
        >
          {/* 準備完了 badge */}
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#F0FDFA', color: '#0a7a72', fontSize: '13px', fontWeight: 700, padding: '4px 14px', borderRadius: '20px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              準備完了
            </span>
          </div>

          <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#0F172A', marginBottom: '10px', lineHeight: '1.4', textAlign: 'center' }}>
            サイトに設置しましょう
          </h2>
          <p style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.7', marginBottom: '28px', textAlign: 'center' }}>
            下のコードをサイトの &lt;/body&gt; の直前に貼り付けるだけで、チャットボットが表示されます。
          </p>

          {/* code section header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#334155' }}>埋め込みコード</span>
            <button
              onClick={handleEmbedCopy}
              style={{ background: embedCopied ? '#DCFCE7' : '#F8FAFC', color: embedCopied ? '#16A34A' : '#475569', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '5px 12px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              {embedCopied ? (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  コピー済み
                </>
              ) : (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                  コピー
                </>
              )}
            </button>
          </div>

          {/* code-box */}
          <div style={{ background: '#0F172A', borderRadius: '10px', padding: '20px 24px', marginBottom: '28px', fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.7', overflowX: 'auto' }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              <span style={{ color: '#38BDF8' }}>&lt;script </span>
              <span style={{ color: '#FBBF24' }}>src</span>
              <span style={{ color: '#38BDF8' }}>=</span>
              <span style={{ color: '#86EFAC' }}>"{WIDGET_URL}/widget.js"</span>
              <span style={{ color: '#38BDF8' }}>&gt;&lt;/script&gt;</span>
              {'\n'}
              <span style={{ color: '#38BDF8' }}>&lt;script&gt;</span>
              {'\n  '}
              <span style={{ color: '#E2E8F0' }}>EntryBot.init({'{'}</span>
              {'\n    '}
              <span style={{ color: '#FBBF24' }}>apiKey</span>
              <span style={{ color: '#E2E8F0' }}>: </span>
              <span style={{ color: '#86EFAC' }}>'{sessionStorage.getItem(`eb_apikey_${createdDomainId}`) || ''}'</span>
              {'\n  '}
              <span style={{ color: '#E2E8F0' }}>{'}'})</span>
              <span style={{ color: '#94A3B8' }}>;</span>
              {'\n'}
              <span style={{ color: '#38BDF8' }}>&lt;/script&gt;</span>
            </pre>
          </div>

          {/* buttons */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setInstallGuideOpen(true)}
              style={{ flex: 1, minWidth: '140px', background: '#fff', color: '#475569', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '14px 20px', fontSize: '15px', fontWeight: 700, cursor: 'pointer' }}
              data-testid="paste-guide-button"
            >
              設置ガイドを見る
            </button>
            <button
              onClick={completeOnboarding}
              style={{ flex: 1, minWidth: '140px', background: '#0a7a72', color: '#fff', border: 'none', borderRadius: '10px', padding: '14px 20px', fontSize: '15px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px rgba(10,122,114,0.25)' }}
            >
              ダッシュボードへ
            </button>
          </div>
        </div>
      )}

      <InstallGuideModal
        isOpen={installGuideOpen}
        onClose={() => setInstallGuideOpen(false)}
        embedCode={generateEmbedCode(sessionStorage.getItem(`eb_apikey_${createdDomainId}`) || '')}
      />
    </div>
  );
};

export default OnboardingWizard;
